/**
 * The v4 printables (0.9.0). Each is plain content for the print stylesheet (#print-root, black on white,
 * page breaks kept off headings and rows): the outing sheet with the walking directions as a QR code made
 * on the device (no network), the two event lists — the Summary, and the Volunteer List that never carries
 * a phone number or an apartment — and the VISIONS workbook cover page. The enrollment packet is
 * features/people/PacketPrint; the close-out report prints the record view (Admin → Review) through
 * printNode(). The outing, event and VISIONS screens (1.1.0–1.3.0) mount these; the words are here now.
 */
import { useTranslation } from 'react-i18next';
import { qrMatrix } from '@/lib/qr';

/** A QR code drawn as SVG squares (the caption beside it says what it opens). */
export function QrCode({ text, label, size = 132 }: { text: string; label: string; size?: number }) {
  const m = qrMatrix(text);
  const n = m.length;
  const quiet = 4;
  const dim = n + quiet * 2;
  const cells: string[] = [];
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (m[y]![x]) cells.push(`M${x + quiet} ${y + quiet}h1v1h-1z`);
  return (
    <svg className="qr-code" role="img" aria-label={label} width={size} height={size} viewBox={`0 0 ${dim} ${dim}`} shapeRendering="crispEdges" data-testid="qr">
      <rect width={dim} height={dim} fill="#fff" />
      <path d={cells.join('')} fill="#000" />
    </svg>
  );
}

export interface OutingPrint {
  title: string;
  dayLabel: string;
  meetTime: string;
  meetPlace: string;
  placeName: string;
  address: string;
  /** Step-by-step walking directions, as written in Approved places. */
  directions: string[];
  /** The map link the QR code opens (walking directions). */
  directionsUrl: string;
  volunteers: string[];
  officePhone: string;
}

export function OutingSheet({ o }: { o: OutingPrint }) {
  const { t } = useTranslation();
  return (
    <div className="print-sheet" data-testid="print-outing">
      <h1>{o.title}</h1>
      <p className="print-lead">
        <b>{o.dayLabel}</b> · {t('print.meetAt', { time: o.meetTime, place: o.meetPlace })}
      </p>
      <div className="print-cols">
        <div>
          <h2>{t('print.where')}</h2>
          <p>
            <b>{o.placeName}</b>
            <br />
            {o.address}
          </p>
          <h2>{t('print.walking')}</h2>
          <ol>
            {o.directions.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ol>
        </div>
        <figure className="print-qr">
          <QrCode text={o.directionsUrl} label={t('print.qrLabel', { place: o.placeName })} />
          <figcaption>{t('print.qrCaption')}</figcaption>
        </figure>
      </div>
      <h2>{t('print.volunteers')}</h2>
      <p>{o.volunteers.join(' · ') || '—'}</p>
      <p className="print-foot">{t('print.office', { phone: o.officePhone })}</p>
    </div>
  );
}

export interface EventPrint {
  title: string;
  dayLabel: string;
  time: string;
  place: string;
}

/** One line of the day-of Summary (the office's copy — the roster manager's Summary sheet). */
export interface EventRsvpLine {
  type: string;
  unit: string;
  name: string;
  household: number;
  guests: number;
  attending: number;
  accessibility: string;
  transportation: string;
  phone: string;
  notes: string;
  standby: boolean;
}

export interface EventSummaryCounts {
  attending: number;
  households: number;
  guests: number;
  volunteers: number;
  standby: number;
}

/** The day-of Summary: counts and everyone coming, with apartments and phones (the office's copy) and a box to tick at the door. */
export function EventSummary({ e, rows, counts }: { e: EventPrint; rows: EventRsvpLine[]; counts: EventSummaryCounts }) {
  const { t } = useTranslation();
  return (
    <div className="print-sheet" data-testid="print-event-summary">
      <h1>{t('print.summaryTitle', { event: e.title })}</h1>
      <p className="print-lead">
        {e.dayLabel} · {e.time} · {e.place}
      </p>
      <p>{t('print.counts', { ...counts })}</p>
      <table className="print-table">
        <caption>{t('print.comingCaption')}</caption>
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">{t('print.col.unit')}</th>
            <th scope="col">{t('print.col.name')}</th>
            <th scope="col">{t('print.col.household')}</th>
            <th scope="col">{t('print.col.guests')}</th>
            <th scope="col">{t('print.col.access')}</th>
            <th scope="col">{t('print.col.transport')}</th>
            <th scope="col">{t('print.col.phone')}</th>
            <th scope="col">{t('print.col.notes')}</th>
            <th scope="col">{t('print.col.here')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.unit}-${r.name}-${i}`}>
              <td>{i + 1}</td>
              <td>{r.unit}</td>
              <td>
                {r.name}
                {r.type !== 'Tenant' ? ` (${r.type})` : ''}
                {r.standby ? ` — ${t('print.standby')}` : ''}
              </td>
              <td>{r.household}</td>
              <td>{r.guests}</td>
              <td>{r.accessibility}</td>
              <td>{r.transportation}</td>
              <td>{r.phone}</td>
              <td>{r.notes}</td>
              <td aria-label={t('print.col.here')}>☐</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One line of the Volunteer List (the roster manager's Volunteer List sheet). */
export interface VolunteerLine {
  /** A volunteer (on a group outing only volunteers are named). */
  volunteer: boolean;
  type: string;
  name: string;
  attending: number;
  accessibility: string;
  transportation: string;
  notes: string;
}

/**
 * The Volunteer List: who is coming and what help they need — never a phone number or an apartment (the type
 * has neither, and the list drops any that arrive anyway: volunteers and tenants never exchange contact details).
 * A group outing never carries a tenant list: then only the volunteers are named, with the tenants as a count.
 */
export function EventVolunteerList({ e, rows, outing = false, tenantCount = 0 }: { e: EventPrint; rows: VolunteerLine[]; outing?: boolean; tenantCount?: number }) {
  const { t } = useTranslation();
  const safe = rows.filter((r) => !outing || r.volunteer).map(({ type, name, attending, accessibility, transportation, notes }) => ({ type, name, attending, accessibility, transportation, notes }));
  return (
    <div className="print-sheet" data-testid="print-event-volunteers">
      <h1>{t('print.volunteerTitle', { event: e.title })}</h1>
      <p className="print-lead">
        {e.dayLabel} · {e.time} · {e.place}
      </p>
      {outing ? <p>{t('print.outingTenants', { count: tenantCount })}</p> : null}
      <table className="print-table">
        <caption>{outing ? t('print.volunteerCaptionOuting') : t('print.volunteerCaption')}</caption>
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">{t('print.col.type')}</th>
            <th scope="col">{t('print.col.name')}</th>
            <th scope="col">{t('print.col.attend')}</th>
            <th scope="col">{t('print.col.access')}</th>
            <th scope="col">{t('print.col.transport')}</th>
            <th scope="col">{t('print.col.notes')}</th>
          </tr>
        </thead>
        <tbody>
          {safe.map((v, i) => (
            <tr key={`${v.name}-${i}`}>
              <td>{i + 1}</td>
              <td>{v.type}</td>
              <td>{v.name}</td>
              <td>{v.attending}</td>
              <td>{v.accessibility}</td>
              <td>{v.transportation}</td>
              <td>{v.notes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface VisionsCoverPrint {
  reportingMonth: string;
  preparedBy: string;
  dateSubmitted: string;
  participants: number;
  consented: number;
  sixtyPlus: number;
  referrals: number;
  activities: number;
}

/** The cover page for the monthly Exhibit A workbook (docs/VISIONS-EXHIBIT-A.md, Sheet 1 header block). */
export function VisionsCover({ v }: { v: VisionsCoverPrint }) {
  const { t } = useTranslation();
  const rows: [string, string | number][] = [
    [t('print.visions.month'), v.reportingMonth],
    [t('print.visions.preparedBy'), v.preparedBy],
    [t('print.visions.submitted'), v.dateSubmitted],
    [t('print.visions.participants'), v.participants],
    [t('print.visions.consented'), v.consented],
    [t('print.visions.sixty'), v.sixtyPlus],
    [t('print.visions.referrals'), v.referrals],
    [t('print.visions.activities'), v.activities],
  ];
  return (
    <div className="print-sheet print-cover" data-testid="print-visions-cover">
      <p className="print-eyebrow">Selis Manor · ABF · VISIONS</p>
      <h1>{t('print.visions.title')}</h1>
      <table className="print-table kv">
        <caption>{t('print.visions.caption')}</caption>
        <tbody>
          {rows.map(([k, val]) => (
            <tr key={k}>
              <th scope="row">{k}</th>
              <td>{val}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="print-foot">{t('print.visions.consentNote')}</p>
    </div>
  );
}
